import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { analyseRound, AREA_META, MONTHS } from '../lib/analysis'
import '../styles/Dashboard.css'

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)
  const name = user?.user_metadata?.name || 'Golfer'
  const handicap = user?.user_metadata?.handicap ?? '—'

  useEffect(() => {
    fetchRounds()
  }, [])

  const fetchRounds = async () => {
    const { data } = await supabase
      .from('rounds')
      .select('*')
      .eq('user_id', user.id)
      .order('played_at', { ascending: false })
      .limit(10)
    setRounds(data || [])
    setLoading(false)
  }

  const last3 = rounds.slice(0, 3)
  const avgScore = last3.length ? Math.round(last3.reduce((s, r) => s + r.score, 0) / last3.length) : null
  const fwAvg = last3.length ? Math.round(last3.reduce((s, r) => s + (r.fw / 14) * 100, 0) / last3.length) : null
  const girAvg = last3.length ? Math.round(last3.reduce((s, r) => s + (r.gir / 18) * 100, 0) / last3.length) : null
  const puttAvg = last3.length ? (last3.reduce((s, r) => s + r.putts, 0) / last3.length).toFixed(1) : null

  // Aggregate weakness scores from last 3 rounds
  const weaknessScores = { driving: 0, approach: 0, shortGame: 0, putting: 0 }
  if (last3.length) {
    last3.forEach(r => {
      const { scores } = analyseRound(r)
      Object.keys(weaknessScores).forEach(k => weaknessScores[k] += scores[k])
    })
    Object.keys(weaknessScores).forEach(k => weaknessScores[k] = Math.round(weaknessScores[k] / last3.length))
  }
  const sortedWeaknesses = Object.entries(weaknessScores).sort((a, b) => b[1] - a[1])

  const scoreColor = (diff) => diff > 5 ? 'var(--red)' : diff <= 0 ? 'var(--green)' : 'var(--white)'
  const scoreDiff = (r) => { const d = r.score - r.par; return d > 0 ? `+${d}` : d === 0 ? 'E' : `${d}` }

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>

  return (
    <div className="dashboard">
      <div className="welcome-row">
        <div className="welcome-text">
          <h2>Hey, {name} 👋</h2>
          <p>{rounds.length === 0 ? 'No rounds logged yet' : `${rounds.length} round${rounds.length > 1 ? 's' : ''} logged`}</p>
        </div>
        <div className="handicap-badge">
          <div className="hcp-num">{handicap}</div>
          <div className="hcp-label">Handicap</div>
        </div>
      </div>

      {rounds.length === 0 ? (
        <div className="cta-card" onClick={() => navigate('/log')}>
          <h3>Start Tracking Your Game</h3>
          <p>Log your first round to get your personalised weakness analysis.</p>
          <button className="btn-white">Log First Round</button>
        </div>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Avg Score</div>
              <div className="stat-value orange">{avgScore}</div>
              <div className="stat-sub">last 3 rounds</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Fairways Hit</div>
              <div className="stat-value">{fwAvg}%</div>
              <div className="stat-sub">last 3 rounds</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">GIR</div>
              <div className="stat-value">{girAvg}%</div>
              <div className="stat-sub">greens in reg</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Avg Putts</div>
              <div className="stat-value">{puttAvg}</div>
              <div className="stat-sub">per round</div>
            </div>
          </div>

          <div className="section-title">Your Weaknesses</div>
          <div className="weakness-list">
            {sortedWeaknesses.map(([key, val]) => {
              const color = val >= 65 ? 'var(--red)' : val >= 40 ? 'var(--yellow)' : 'var(--green)'
              return (
                <div className="weakness-row" key={key}>
                  <div className="weakness-header">
                    <span className="weakness-name">{AREA_META[key].label}</span>
                    <span className="weakness-score" style={{ color }}>{100 - val}</span>
                  </div>
                  <div className="weakness-bar-bg">
                    <div className="weakness-bar-fill" style={{ width: `${val}%`, background: color }} />
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      <div className="section-title" style={{ marginTop: rounds.length > 0 ? 24 : 0 }}>Recent Rounds</div>
      {rounds.length === 0 ? (
        <div className="empty-state">
          <div className="big-icon">⛳</div>
          <h3>No Rounds Yet</h3>
          <p>Log your first round to see your stats and analysis</p>
          <button className="btn-orange" onClick={() => navigate('/log')}>Log a Round</button>
        </div>
      ) : (
        <div className="rounds-list">
          {rounds.map(r => {
            const d = new Date(r.played_at)
            const diff = r.score - r.par
            return (
              <div className="round-card" key={r.id} onClick={() => navigate(`/results/${r.id}`)}>
                <div className="round-date-block">
                  <div className="round-day">{d.getDate()}</div>
                  <div className="round-month">{MONTHS[d.getMonth()]}</div>
                </div>
                <div className="round-info">
                  <div className="round-course">{r.course}</div>
                  <div className="round-meta">{r.fw}/14 FW · {r.gir} GIR · {r.putts} putts</div>
                </div>
                <div className="round-score-chip" style={{ color: scoreColor(diff) }}>{scoreDiff(r)}</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
